import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { isValidCpf } from '../../utils/isValidCpf';
import { isValidCnpj } from '../../utils/isValidCnpj';

export type PaymentMethod = 'credit_card' | 'boleto' | 'two_cards' | 'card_boleto';

const HOMOLOG_TEST_CPF = '11111111111';

interface PaymentMethodsFormProps {
  totalAmount: number;
  maxInstallments?: number;
  onSubmit?: (data: any) => void | Promise<void>;
  allowedMethods?: PaymentMethod[];
}

const PaymentMethodsForm: React.FC<PaymentMethodsFormProps> = ({
  totalAmount = 10000,
  maxInstallments = 12,
  onSubmit,
  allowedMethods,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    allowedMethods?.[0] || 'credit_card',
  );
  const [submitting, setSubmitting] = useState(false);
  const [cardData, setCardData] = useState({
    number: '',
    holder: '',
    document: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
  });
  const [secondCardData, setSecondCardData] = useState({
    number: '',
    holder: '',
    document: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
  });
  const [installments, setInstallments] = useState(1);
  const [secondInstallments, setSecondInstallments] = useState(1);
  const [firstPaymentAmount, setFirstPaymentAmount] = useState(totalAmount / 2);
  const [errors, setErrors] = useState<string[]>([]);

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^\d]/gi, '');
    const parts = [];
    for (let i = 0; i < v.length; i += 4) {
      parts.push(v.substring(i, i + 4));
    }
    return parts.join(' ');
  };

  const formatCpfCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);

    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const formatCentsToMasked = (cents: number): string => {
    const safeCents = Math.max(0, Math.round(cents || 0));
    const padded = safeCents.toString().padStart(3, '0');
    const intPart = padded.slice(0, -2).replace(/^0+(?=\d)/, '');
    const decPart = padded.slice(-2);
    return `${intPart},${decPart}`;
  };

  const handleMaskedAmountChange = (raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '');
    const cents = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
    setFirstPaymentAmount(cents);
    setErrors([]);
  };

  const handleMaskedAmountBlur = () => {
    const maxCents = Math.max(0, totalAmount - 100);
    const minCents = Math.min(100, maxCents);
    setFirstPaymentAmount((prev) => Math.min(Math.max(prev, minCents), maxCents));
  };

  const validateCardData = (card: any, cardName: string): string[] => {
    const cardErrors: string[] = [];

    const cleanNumber = card.number.replace(/\s/g, '');
    if (!cleanNumber || cleanNumber.length === 0) {
      cardErrors.push(`${cardName}: Número do cartão é obrigatório`);
    } else if (cleanNumber.length < 13) {
      cardErrors.push(`${cardName}: Número do cartão inválido (mínimo 13 dígitos)`);
    } else if (cleanNumber.length > 19) {
      cardErrors.push(`${cardName}: Número do cartão inválido (máximo 19 dígitos)`);
    }

    if (!card.holder || card.holder.trim().length === 0) {
      cardErrors.push(`${cardName}: Nome do titular é obrigatório`);
    } else if (card.holder.trim().length < 3) {
      cardErrors.push(`${cardName}: Nome do titular muito curto`);
    }

    const cleanDocument = (card.document || '').replace(/\D/g, '');
    const isHomologTestCpf = process.env.REACT_APP_SINTEGRA_HOMO === '1' && cleanDocument === HOMOLOG_TEST_CPF;
    if (!cleanDocument) {
      cardErrors.push(`${cardName}: CPF/CNPJ do titular é obrigatório`);
    } else if (!isHomologTestCpf && !isValidCpf(cleanDocument) && !isValidCnpj(cleanDocument)) {
      cardErrors.push(`${cardName}: CPF/CNPJ do titular inválido`);
    }

    if (!card.expiryMonth || card.expiryMonth.length === 0) {
      cardErrors.push(`${cardName}: Mês de vencimento é obrigatório`);
    } else {
      const month = parseInt(card.expiryMonth, 10);
      if (Number.isNaN(month) || month < 1 || month > 12) {
        cardErrors.push(`${cardName}: Mês inválido (01-12)`);
      }
    }

    if (!card.expiryYear || card.expiryYear.length === 0) {
      cardErrors.push(`${cardName}: Ano de vencimento é obrigatório`);
    } else if (!/^\d{4}$/.test(card.expiryYear)) {
      cardErrors.push(`${cardName}: Ano deve ter 4 dígitos (ex: ${new Date().getFullYear()})`);
    } else {
      const year = parseInt(card.expiryYear, 10);
      const currentYear = new Date().getFullYear();
      if (year < currentYear || year > currentYear + 20) {
        cardErrors.push(`${cardName}: Ano inválido`);
      }
    }

    if (!card.cvc || card.cvc.length === 0) {
      cardErrors.push(`${cardName}: CVV é obrigatório`);
    } else if (card.cvc.length < 3 || card.cvc.length > 4) {
      cardErrors.push(`${cardName}: CVV inválido (3-4 dígitos)`);
    }

    return cardErrors;
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (paymentMethod === 'credit_card') {
      newErrors.push(...validateCardData(cardData, 'Cartão'));
    } else if (paymentMethod === 'boleto') {
      // Boleto não precisa de validação adicional
    } else if (paymentMethod === 'two_cards') {
      newErrors.push(...validateCardData(cardData, '1º Cartão'));
      newErrors.push(...validateCardData(secondCardData, '2º Cartão'));

      if (firstPaymentAmount <= 0 || firstPaymentAmount >= totalAmount) {
        newErrors.push('Divida o valor entre os dois cartões (ambos devem ter valor maior que 0)');
      }
    } else if (paymentMethod === 'card_boleto') {
      newErrors.push(...validateCardData(cardData, 'Cartão'));

      if (firstPaymentAmount <= 0 || firstPaymentAmount >= totalAmount) {
        newErrors.push('Divida o valor entre o cartão e o boleto (ambos devem ter valor maior que 0)');
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (onSubmit) {
      try {
        setSubmitting(true);
        await onSubmit({
          method: paymentMethod,
          amount: totalAmount,
          cardData: paymentMethod === 'credit_card' || paymentMethod === 'two_cards' || paymentMethod === 'card_boleto' ? cardData : undefined,
          secondCardData: paymentMethod === 'two_cards' ? secondCardData : undefined,
          installments: paymentMethod === 'credit_card' || paymentMethod === 'two_cards' || paymentMethod === 'card_boleto' ? installments : undefined,
          secondInstallments: paymentMethod === 'two_cards' ? secondInstallments : undefined,
          firstPaymentAmount: paymentMethod === 'two_cards' || paymentMethod === 'card_boleto' ? firstPaymentAmount : undefined,
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const secondAmount = totalAmount - firstPaymentAmount;
  const submitLabel = paymentMethod === 'boleto' ? 'Gerar Boleto' : 'Confirmar Pagamento';

  const paymentMethodOptions: Array<{ value: PaymentMethod; label: string }> = [
    { value: 'credit_card', label: 'Cartão de Crédito' },
    { value: 'boleto', label: 'Boleto' },
    { value: 'two_cards', label: 'Dois Cartões' },
    { value: 'card_boleto', label: 'Cartão + Boleto' },
  ];

  const visibleMethodOptions = allowedMethods
    ? paymentMethodOptions.filter((option) => allowedMethods.includes(option.value))
    : paymentMethodOptions;

  return (
    <Form onSubmit={handleSubmit}>
      {errors.length > 0 && (
        <Alert variant="danger" className="mb-4">
          <h6 className="mb-2">❌ Preencha corretamente:</h6>
          <ul className="mb-0 ps-3">
            {errors.map((error, idx) => (
              <li key={idx} className="small">
                {error}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {visibleMethodOptions.length > 1 && (
      <Form.Group className="mb-4">
        <Form.Label className="fw-bold fs-5">Método de Pagamento</Form.Label>
        <div
          className="d-flex flex-wrap"
          style={{ rowGap: '8px', columnGap: '8px' }}
        >
          {visibleMethodOptions.map((option) => {
            const isSelected = paymentMethod === option.value;
            return (
              <button
                key={option.value}
                type="button"
                id={`method_${option.value}`}
                aria-pressed={isSelected}
                onClick={() => {
                  setPaymentMethod(option.value);
                  setErrors([]);
                }}
                style={{
                  flex: '1 1 auto',
                  minWidth: '98px',
                  padding: '6px 10px',
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap',
                  border: isSelected ? '2px solid #0d6efd' : '1px solid #ced4da',
                  background: isSelected ? '#e7f1ff' : '#fff',
                  color: isSelected ? '#0d6efd' : '#212529',
                  fontWeight: isSelected ? 600 : 400,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'border-color .15s ease, background-color .15s ease, color .15s ease',
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </Form.Group>
      )}

      <Card className="mb-4 bg-light">
        <Card.Body>
          <Row>
            <Col>
              <small className="text-muted">Valor Total</small>
              <h5 className="mb-0">R$ {(totalAmount / 100).toFixed(2)}</h5>
            </Col>
            {(paymentMethod === 'two_cards' || paymentMethod === 'card_boleto') && (
              <>
                <Col>
                  <small className="text-muted">
                    {paymentMethod === 'two_cards' ? '1º Cartão' : 'Cartão'}
                  </small>
                  <h5 className="mb-0">R$ {(firstPaymentAmount / 100).toFixed(2)}</h5>
                </Col>
                <Col>
                  <small className="text-muted">
                    {paymentMethod === 'two_cards' ? '2º Cartão' : 'Boleto'}
                  </small>
                  <h5 className="mb-0">R$ {(secondAmount / 100).toFixed(2)}</h5>
                </Col>
              </>
            )}
          </Row>
        </Card.Body>
      </Card>

      {(paymentMethod === 'two_cards' || paymentMethod === 'card_boleto') && (
        <Form.Group className="mb-4 p-3 border rounded bg-light bg-opacity-10">
          <Form.Label className="fw-bold">
            {paymentMethod === 'two_cards' ? 'Dividir entre 2 Cartões' : 'Valor para Cartão'}
          </Form.Label>
          <Form.Control
            type="text"
            inputMode="numeric"
            value={formatCentsToMasked(firstPaymentAmount)}
            onChange={(e) => handleMaskedAmountChange(e.target.value)}
            onBlur={handleMaskedAmountBlur}
          />
          <small className="text-muted d-block mt-2">
            Valor entre R$ 1,00 e R$ {((totalAmount - 100) / 100).toFixed(2).replace('.', ',')}
          </small>
          <small className="text-muted d-block mt-2">
            {paymentMethod === 'two_cards'
              ? `1º Cartão: R$ ${(firstPaymentAmount / 100).toFixed(2)} | 2º Cartão: R$ ${(secondAmount / 100).toFixed(2)}`
              : `Cartão: R$ ${(firstPaymentAmount / 100).toFixed(2)} | Boleto: R$ ${(secondAmount / 100).toFixed(2)}`}
          </small>
        </Form.Group>
      )}

      {(paymentMethod === 'credit_card' || paymentMethod === 'two_cards' || paymentMethod === 'card_boleto') && (
        <div className="mb-4 p-3 border rounded">
          <h6 className="mb-3">
            {paymentMethod === 'credit_card' ? 'Dados do Cartão' : '1º Cartão'}
          </h6>

          <Form.Group className="mb-3">
            <Form.Label className="small">Número do Cartão</Form.Label>
            <Form.Control
              type="text"
              placeholder="0000 0000 0000 0000"
              value={cardData.number}
              onChange={(e) => {
                setCardData({ ...cardData, number: formatCardNumber(e.target.value) });
                setErrors([]);
              }}
              maxLength={19}
              isInvalid={errors.some((e) => e.includes('Número do cartão'))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small">Titular</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome Completo"
              value={cardData.holder}
              onChange={(e) => {
                setCardData({ ...cardData, holder: e.target.value.toUpperCase() });
                setErrors([]);
              }}
              isInvalid={errors.some((e) => e.includes('Nome do titular'))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small">CPF/CNPJ do Titular</Form.Label>
            <Form.Control
              type="text"
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              value={cardData.document}
              onChange={(e) => {
                setCardData({ ...cardData, document: formatCpfCnpj(e.target.value) });
                setErrors([]);
              }}
              maxLength={18}
              isInvalid={errors.some((e) => e.includes('CPF/CNPJ do titular'))}
            />
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small">Mês</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="MM"
                  value={cardData.expiryMonth}
                  onChange={(e) => {
                    let val = e.target.value.slice(0, 2);
                    if (val && parseInt(val, 10) > 12) val = '12';
                    setCardData({ ...cardData, expiryMonth: val });
                    setErrors([]);
                  }}
                  maxLength={2}
                  isInvalid={errors.some((e) => e.includes('Mês'))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small">Ano</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="AAAA"
                  value={cardData.expiryYear}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setCardData({ ...cardData, expiryYear: digits });
                    setErrors([]);
                  }}
                  maxLength={4}
                  isInvalid={errors.some((e) => e.includes('Ano'))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small">CVV</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="CVV"
                  value={cardData.cvc}
                  onChange={(e) => {
                    setCardData({ ...cardData, cvc: e.target.value.slice(0, 4) });
                    setErrors([]);
                  }}
                  maxLength={4}
                  isInvalid={errors.some((e) => e.includes('CVV'))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="small">Parcelamento</Form.Label>
            <Form.Control
              as="select"
              value={installments}
              onChange={(e) => setInstallments(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: maxInstallments }, (_, i) => i + 1).map((num) => {
                const amount = paymentMethod === 'credit_card' ? totalAmount : firstPaymentAmount;
                return (
                  <option key={num} value={num}>
                    {num}x de R$ {((amount / num) / 100).toFixed(2)}
                  </option>
                );
              })}
            </Form.Control>
          </Form.Group>
        </div>
      )}

      {paymentMethod === 'two_cards' && (
        <div className="mb-4 p-3 border rounded">
          <h6 className="mb-3">2º Cartão</h6>

          <Form.Group className="mb-3">
            <Form.Label className="small">Número do Cartão</Form.Label>
            <Form.Control
              type="text"
              placeholder="0000 0000 0000 0000"
              value={secondCardData.number}
              onChange={(e) => {
                setSecondCardData({ ...secondCardData, number: formatCardNumber(e.target.value) });
                setErrors([]);
              }}
              maxLength={19}
              isInvalid={errors.some((e) => e.includes('2º Cartão') && e.includes('Número'))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small">Titular</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome Completo"
              value={secondCardData.holder}
              onChange={(e) => {
                setSecondCardData({ ...secondCardData, holder: e.target.value.toUpperCase() });
                setErrors([]);
              }}
              isInvalid={errors.some((e) => e.includes('2º Cartão') && e.includes('titular'))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small">CPF/CNPJ do Titular</Form.Label>
            <Form.Control
              type="text"
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              value={secondCardData.document}
              onChange={(e) => {
                setSecondCardData({ ...secondCardData, document: formatCpfCnpj(e.target.value) });
                setErrors([]);
              }}
              maxLength={18}
              isInvalid={errors.some((e) => e.includes('2º Cartão') && e.includes('CPF'))}
            />
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small">Mês</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="MM"
                  value={secondCardData.expiryMonth}
                  onChange={(e) => {
                    let val = e.target.value.slice(0, 2);
                    if (val && parseInt(val, 10) > 12) val = '12';
                    setSecondCardData({ ...secondCardData, expiryMonth: val });
                    setErrors([]);
                  }}
                  maxLength={2}
                  isInvalid={errors.some((e) => e.includes('2º Cartão') && e.includes('Mês'))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small">Ano</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="AAAA"
                  value={secondCardData.expiryYear}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setSecondCardData({ ...secondCardData, expiryYear: digits });
                    setErrors([]);
                  }}
                  maxLength={4}
                  isInvalid={errors.some((e) => e.includes('2º Cartão') && e.includes('Ano'))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small">CVV</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="CVV"
                  value={secondCardData.cvc}
                  onChange={(e) => {
                    setSecondCardData({ ...secondCardData, cvc: e.target.value.slice(0, 4) });
                    setErrors([]);
                  }}
                  maxLength={4}
                  isInvalid={errors.some((e) => e.includes('2º Cartão') && e.includes('CVV'))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="small">Parcelamento</Form.Label>
            <Form.Control
              as="select"
              value={secondInstallments}
              onChange={(e) => setSecondInstallments(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: maxInstallments }, (_, i) => i + 1).map((num) => (
                <option key={num} value={num}>
                  {num}x de R$ {((secondAmount / num) / 100).toFixed(2)}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
      )}

      {paymentMethod === 'boleto' && (
        <div className="mb-4 p-3 border rounded bg-warning bg-opacity-10">
          <h6>Boleto Bancário</h6>
          <p className="mb-0 small">
            Você receberá o boleto por e-mail e terá até 2 dias úteis para realizar o pagamento.
          </p>
        </div>
      )}

      {paymentMethod === 'card_boleto' && (
        <div className="mb-4 p-3 border rounded bg-warning bg-opacity-10">
          <h6>Boleto Bancário</h6>
          <p className="mb-0 small">
            Valor: <strong>R$ {(secondAmount / 100).toFixed(2)}</strong>
          </p>
          <p className="mb-0 small">
            Você receberá o boleto por e-mail e terá até 2 dias úteis para realizar o pagamento.
          </p>
        </div>
      )}

      <Button
        variant="primary"
        type="submit"
        size="lg"
        className="w-100"
        disabled={submitting}
      >
        {submitting ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Processando...
          </>
        ) : submitLabel}
      </Button>
    </Form>
  );
};

export default PaymentMethodsForm;
